import { useQueries } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useBulkDeleteClasses } from '~entities/classes/api/bulk-delete-classes';
import { useDeleteClass } from '~entities/classes/api/delete-class';
import { getClassStudentCountQueryOptions } from '~entities/classes/api/get-class-student-count';
import { type ClassSearchDetails } from '~entities/classes/modal/class-search';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import { Label } from '~/shared/shadcn/ui/label';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

interface DeleteClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedClasses: Array<ClassSearchDetails>;
  onClose: () => void;
  isBulkDelete?: boolean;
}

export const DeleteClassDialog = ({
  open,
  onOpenChange,
  selectedClasses,
  onClose,
  isBulkDelete = false,
}: DeleteClassDialogProps) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const { mutateAsync: deleteMutation, isPending: isDeletePending } =
    useDeleteClass();
  const { mutateAsync: bulkDeleteMutation, isPending: isBulkDeletePending } =
    useBulkDeleteClasses();

  // Use useQueries instead of mapping useQuery
  const studentCountQueries = useQueries({
    queries: selectedClasses.map((classItem) => ({
      ...getClassStudentCountQueryOptions(classItem._id, !!classItem),
    })),
  });

  const handleClose = () => {
    setDeleteConfirmText('');
    onClose();
  };

  const handleDelete = async () => {
    if (isBulkDelete) {
      try {
        await bulkDeleteMutation({
          classIds: selectedClasses.map((classItem) => classItem._id),
        });
        toast.success('Classes deleted successfully');
        handleClose();
      } catch (error) {
        console.error('Error deleting classes:', error);
        toast.error('Failed to delete classes');
      }
    } else {
      const classId = selectedClasses[0]?._id;
      if (!classId) {
        toast.error('No class selected for deletion.');
        console.error('Attempted to delete with no classId.');
        return;
      }
      try {
        await deleteMutation({ classId });
        toast.success('Class deleted successfully');
        handleClose();
      } catch (error) {
        console.error('Error deleting class:', error);
        toast.error('Failed to delete class');
      }
    }
  };

  // Check if any selected class has enrolled students
  const hasEnrolledStudents = studentCountQueries.some(
    (query) => (query.data?.count ?? 0) > 0,
  );

  // Get details of classes with enrolled students
  const classesWithStudents = selectedClasses
    .map((classItem, index) => ({
      name: classItem.title,
      studentCount: studentCountQueries[index]?.data?.count ?? 0,
    }))
    .filter((classItem) => classItem.studentCount > 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {isBulkDelete ? 'Classes' : 'Class'}</DialogTitle>
        </DialogHeader>

        {!hasEnrolledStudents ? (
          <>
            <DialogDescription>
              {isBulkDelete
                ? 'Are you sure you want to delete these classes? This action cannot be undone.'
                : 'Are you sure you want to delete this class? This action cannot be undone.'}
            </DialogDescription>
            <div>
              <Label htmlFor='confirmDelete'>
                Type &quot;delete&quot; to confirm:
              </Label>
              <Input
                id='confirmDelete'
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                }}
                className='mt-2'
              />
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={handleClose}
                disabled={isBulkDeletePending || isDeletePending}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                disabled={
                  deleteConfirmText !== 'delete' ||
                  isBulkDeletePending ||
                  isDeletePending
                }
                onClick={handleDelete}
              >
                {(isBulkDeletePending || isDeletePending) && (
                  <Loader2 className='animate-spin' />
                )}
                {isBulkDelete ? 'Delete Classes' : 'Delete Class'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogDescription className='text-destructive'>
              {isBulkDelete
                ? 'The following classes have enrolled students and cannot be deleted:'
                : 'This class has enrolled students and cannot be deleted:'}
              <ul className='mt-2 list-disc pl-4'>
                {classesWithStudents.map((classItem) => (
                  <li key={classItem.name}>
                    <span className='font-medium text-black'>
                      {classItem.name}
                    </span>
                    : {classItem.studentCount}{' '}
                    {classItem.studentCount === 1 ? 'student' : 'students'}
                  </li>
                ))}
              </ul>
              Please remove all enrolled students before deleting{' '}
              {isBulkDelete ? 'these classes' : 'this class'}.
            </DialogDescription>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
